// Spec iter132 pt.3 (Feb 2026) — Édition rapide de la priorité d'une
// tâche depuis le tableau (clic sur le badge Prio → petit menu 3 choix).
import { useState } from "react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import PriorityBadge, { PRIORITY_LABEL } from "./PriorityBadge";
import { Minus, ArrowUp, Flame, Check } from "lucide-react";

const CHOICES = [
  { value: "moyen",    label: "Normal",   Icon: Minus,   color: "#22C55E" },
  { value: "urgent",   label: "Élevée",   Icon: ArrowUp, color: "#F97316" },
  { value: "immediat", label: "Immédiat", Icon: Flame,   color: "#EF4444" },
];

export default function PriorityQuickPicker({ task, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localPriority, setLocalPriority] = useState(task.priority || "moyen");

  const update = async (value) => {
    if (value === localPriority) { setOpen(false); return; }
    setSaving(true);
    // Optimistic UI
    const previous = localPriority;
    setLocalPriority(value);
    setOpen(false);
    try {
      await api.patch(`/tasks/${task.id}`, { priority: value });
      toast.success(`Priorité → ${PRIORITY_LABEL[value] || value}`);
      onChanged && onChanged(task.id, value);
    } catch (e) {
      setLocalPriority(previous);
      toast.error("Impossible de modifier la priorité", {
        description: e.response?.data?.detail || "",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => !saving && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded transition-colors hover:bg-white/5 -mx-1 px-1 -my-0.5 py-0.5"
          data-testid={`priority-quick-trigger-${task.id}`}
          title="Cliquez pour changer la priorité"
          disabled={saving}
        >
          <PriorityBadge priority={localPriority} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-40 p-1"
        style={{ background: "#141414", border: "1px solid #2A2A2A" }}
        data-testid={`priority-quick-menu-${task.id}`}
      >
        {CHOICES.map((c) => {
          const Icon = c.Icon;
          const active = localPriority === c.value;
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => update(c.value)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] font-semibold hover:bg-white/5 transition-colors text-left"
              style={{ color: c.color }}
              data-testid={`priority-quick-opt-${task.id}-${c.value}`}
            >
              <Icon size={15} strokeWidth={2.2} />
              <span className="flex-1">{c.label}</span>
              {active && <Check size={13} strokeWidth={2.6} style={{ color: c.color }} />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
